import { ScriptRegistryProvider } from '@clover-util/script-registry';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Router } from 'react-router-dom';
import WebFont from 'webfontloader';

import { i18nInstance } from 'i18n';
import { history } from 'store/history';

import { ContentEntriesProvider } from 'components/ContentEntriesProvider/ContentEntriesContext';
import { BuildYourOwnProvider } from 'features/build-your-own/hooks/useBuildYourOwn';
import { ValidatedProgramPromotionProvider } from 'hooks/ProgramPromotion/useValidatedProgramPromotion';
import { ResellerProvider } from 'hooks/Reseller/useReseller';
import { PartnerAttributionProvider } from 'services/analytics/PartnerAttribution';
import { App } from '../App/App';
import { BusyLoader } from '../BusyLoader/BusyLoader';
import { DynamicModalProvider } from '../DynamicModal/DynamicModal';
import { FeatureFlagsProvider } from '../FeatureFlags/FeatureFlags';
import { Matryoshka } from '../Matryoshka/Matryoshka';
import { NavBarProvider } from '../NavBarContext/NavBarContext';
import { CheckoutProvider } from 'components/Checkout/CheckoutProvider';
import { PaymentsComparisonModalProvider } from '../PaymentsComparisonLink/PaymentsComparisonModal/PaymentsComparisonModalContext';
import { ScrollPositionProvider } from '../ScrollPositionProvider/ScrollPositionContext';
import { SidebarProvider } from '../SidebarContext/SidebarContext';
import { SubscriptionsToggleOrderProvider } from 'hooks/useSubscriptionsToggleOrder';
import { PaymentsToggleProvider } from 'components/Cart/OrderSummary/PaymentsToggle/usePaymentsToggle/usePaymentsToggle';
import { FetchingPromoInternalProvider } from '../Cart/OrderSummary/PromoSummary/FetchingPromoContext/useFetchingPromoInternal';
import { FiMerchantProvider } from 'hooks/useFiMerchant';
import { IntercomProvider } from 'hooks/useIntercom';
import { CartProvider } from '../Cart/hooks/CartProvider';

export class Root extends React.Component {
  componentDidMount() {
    if (document.documentElement && !document.documentElement.classList.contains('wf-active')) {
      WebFont.load({
        loading() {
          Array.from(document.getElementsByClassName('material-icons')).forEach(element => {
            element.classList.add('wf-loading');
          });
        },
        active() {
          Array.from(document.getElementsByClassName('wf-loading')).forEach(element => {
            element.classList.remove('wf-loading');
            element.classList.add('wf-active');
          });
        },
        google: {
          // Load Material Icons options
          // https://github.com/typekit/webfontloader/issues/409
          families: ['Material Icons&display=block'],
        },
      });
    }
  }

  render() {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
        },
      },
    });

    return (
      <Matryoshka
        wrappers={[
          // React.StrictMode
          ({ children }) => <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>,
          ({ children }) => <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>,
          ({ children }) => <Suspense fallback={<BusyLoader />}>{children}</Suspense>,
          ({ children }) => <Router history={history}>{children}</Router>,
          ContentEntriesProvider,
          ScrollPositionProvider,
          ResellerProvider,
          PartnerAttributionProvider,
          FeatureFlagsProvider,
          SubscriptionsToggleOrderProvider,
          IntercomProvider,
          PaymentsComparisonModalProvider,
          SidebarProvider,
          NavBarProvider,
          FetchingPromoInternalProvider,
          FiMerchantProvider,
          CartProvider,
          CheckoutProvider,
          DynamicModalProvider,
          ValidatedProgramPromotionProvider,
          ScriptRegistryProvider,
          BuildYourOwnProvider,
          PaymentsToggleProvider,
          App,
        ]}
      />
    );
  }
}
